'use client';

/**
 * AnalyticsCovSynopsisCard — the generic two-register LLM narrator over
 * the coverage aggregates (headline ≈50w + body ≈200w, Gemini
 * schema-enforced). Mounted on the mode9_coverage rail; loads in
 * parallel with the canvas. Explains the coverage graph and defers the
 * numbers to the cards.
 *
 * Mirrors system_obeya/synopsis-card.tsx: 60-min stale (synthesis is
 * expensive), manual Refresh, chrome labels piggyback on useCoverageView
 * (deduped by React Query). Calls `analytics.synopsize`.
 *
 * @see klyde_edge/docs/llm_responses_concept.md
 * @see klyde_edge/klyde_intelligence/templates/synopsis.py
 */

import { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, Copy, Check } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { api } from '@/lib/api/client';
import { useCoverageView } from './use-coverage-view';

interface Synopsis {
  headline: string;
  body: string;
  model?: string;
  generated_at?: string;
}

const STALE_MS = 60 * 60 * 1000;

export function AnalyticsCovSynopsisCard() {
  const strategy = useCapabilityStore((s) => s.strategyName('analytics', 'synopsize'));
  const lang = useLanguageStore((s) => s.lang);
  const queryClient = useQueryClient();

  // Chrome labels dedupe with the canvas coverage cards.
  const { data: coverage } = useCoverageView();
  const L = coverage?.labels ?? {};

  const queryKey = ['analytics-synopsis', strategy, lang];
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.getStrategyData<Synopsis>(
        strategy ?? 'analytics_synopsis',
        { ...(lang ? { lang } : {}) },
      );
      if (!res.success) throw new Error(res.error || `${strategy} failed`);
      return res.data;
    },
    staleTime: STALE_MS,
  });

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!data) return;
    void navigator.clipboard.writeText(`${data.headline}\n\n${data.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const handleRefresh = () => void queryClient.invalidateQueries({ queryKey });
  const isRefreshing = isFetching && !isLoading;

  useCardExport({
    label: L.cov_synopsis_title ?? 'Coverage Synopsis',
    text: data ? `${data.headline}\n\n${data.body}` : (L.cov_synopsis_empty ?? ''),
    markdown: data ? `**${data.headline}**\n\n${data.body}` : '',
    json: data ? { headline: data.headline, body: data.body, model: data.model ?? '' } : undefined,
    content_key: 'analytics_cov_synopsis',
    surface: 'rail',
    status: data ? 'loaded' : isLoading ? 'loading' : error ? 'error' : 'idle',
    query_key: queryKey,
  });

  return (
    <div className="bg-card rounded-xl border border-border p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className={text.title}>{L.cov_synopsis_title ?? 'Coverage Synopsis'}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            disabled={!data}
            title={L.cov_synopsis_copy ?? 'Copy'}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-40 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            title={L.cov_synopsis_refresh ?? 'Refresh'}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-40 transition-colors"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className={cn(text.muted, 'flex items-center gap-2 py-4')}>
          <Loader2 className="w-4 h-4 animate-spin" />
          {L.loading_message ?? 'Loading…'}
        </div>
      ) : error ? (
        <div className={cn(text.muted)}>{L.cov_synopsis_empty ?? 'No synopsis yet.'}</div>
      ) : data ? (
        <>
          <p className={cn(text.body, 'font-medium leading-relaxed')}>{data.headline}</p>
          <p className={cn(text.muted, 'leading-relaxed whitespace-pre-line')}>{data.body}</p>
          {data.model && <p className={cn(text.muted, 'text-[0.7em] opacity-60')}>{data.model}</p>}
        </>
      ) : (
        <div className={cn(text.muted)}>{L.cov_synopsis_empty ?? 'No synopsis yet.'}</div>
      )}
    </div>
  );
}
